import { Stack, Typography } from "@mui/material";
import {
  AlertCircle,
  Dialog,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React from "react";
import { useTranslation } from "react-i18next";
import { SnapshotIn } from "mobx-state-tree";
import { PendingSurveyAssignmentErrorModel } from "../../models/PendingSurveyAssignmentErrorModel";

interface PendingSurveyAssignmentErrorDialogProps {
  pendingSurveyAssignmentError: SnapshotIn<
    typeof PendingSurveyAssignmentErrorModel
  >;
  closeDialog: VoidFunction;
}

interface RespondentsPendingSurveyStatistic {
  label: string;
  count: number;
}

const DIALOG_WIDTH = "560px";
const ALERT_ICON_SIZE = "20px";
const RESPONDENTS_PENDING_SURVEY_STATISTIC_WRAPPER_WIDTH = "274px";

export const PendingSurveyAssignmentErrorDialog: React.FC<
  PendingSurveyAssignmentErrorDialogProps
> = ({ pendingSurveyAssignmentError, closeDialog }) => {
  const { t } = useTranslation();
  const spacing = useSpacing();
  const tokens = useFoundationColorTokens();
  const typography = useTypography();

  const respondentsPendingSurveyStatistics: RespondentsPendingSurveyStatistic[] =
    [
      {
        label: t("emailCycles.respondentsSelected"),
        count: pendingSurveyAssignmentError.respondentsSelected,
      },
      {
        label: t("emailCycles.respondentsAssigned"),
        count: pendingSurveyAssignmentError.respondentsAssigned,
      },
      {
        label: t("emailCycles.respondentsNotAssigned"),
        count: pendingSurveyAssignmentError.respondentsUnassigned,
      },
    ];

  return (
    <Dialog
      title={t("emailCycles.pendingSurveyAssignmentErrorTitle")}
      secondaryButtonText={t("common.close")}
      onSecondaryButtonClick={closeDialog}
      width={DIALOG_WIDTH}
      open
      disableBackdropClick={true}
    >
      <Stack gap={spacing.spaceLG}>
        <Stack
          direction="row"
          gap={spacing.spaceXS}
          color={tokens.labelWarning}
        >
          <AlertCircle
            height={ALERT_ICON_SIZE}
            width={ALERT_ICON_SIZE}
            style={{ flexShrink: 0 }}
          />
          <Typography {...typography.b2}>
            {t("emailCycles.pendingSurveyAssignmentErrorMessage")}
          </Typography>
        </Stack>

        <Stack width={RESPONDENTS_PENDING_SURVEY_STATISTIC_WRAPPER_WIDTH}>
          {respondentsPendingSurveyStatistics.map(({ label, count }, index) => (
            <Stack key={index} direction="row" paddingBlock={spacing.spaceXS}>
              <Typography
                {...typography.b1}
                color={tokens.labelSubtle}
                sx={{ flex: 1 }}
              >
                {label}
              </Typography>
              <Typography {...typography.s1} color={tokens.label}>
                {count}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Dialog>
  );
};
